import type { Directive, DirectiveBinding } from 'vue'

interface MoneyOptions {
  decimal?: number // 保留几位小数
  thousands?: boolean // 是否使用千分位分隔符
  prefix?: string // 前缀，如 ¥
  suffix?: string // 后缀，如 元
}

function formatMoney(value: number | undefined | null, options: MoneyOptions = {}) {
  const { decimal = 2, thousands = false, prefix = '', suffix = '' } = options

  if (value === undefined || value === null || isNaN(Number(value))) {
    return `${prefix}0.00${suffix}`
  }

  let num = Number(value).toFixed(decimal)
  if (thousands) {
    const parts = num.split('.')
    parts[0] = parts[0].replace(thousandsRegex, ',')
    num = parts.join('.')
  }

  return `${prefix}${num}${suffix}`
}

interface CustomHTMLElement extends HTMLElement {
  _observer?: MutationObserver
  _lastValue?: string
  _formatFn?: (value: string) => void
}

// 缓存正则表达式
const thousandsRegex = /\B(?=(\d{3})+(?!\d))/g

const moneyFormat: Directive = {
  mounted(el: CustomHTMLElement, binding: DirectiveBinding<MoneyOptions>) {
    const formatValue = () => {
      // 获取原始值，使用 innerHTML 以获取实际内容
      const rawValue = el.innerHTML?.trim()
      if (!rawValue) return

      // 移除可能存在的千分位符号，以获取纯数字
      const cleanValue = rawValue.replace(/,/g, '')

      // 只有当值真正改变时才更新
      if (cleanValue !== el._lastValue) {
        el._lastValue = cleanValue
        const formattedValue = formatMoney(Number(cleanValue), binding.value)
        // 使用 innerHTML 更新显示
        el.innerHTML = formattedValue
      }
    }

    // 初始格式化
    formatValue()
  },

  updated(el: CustomHTMLElement, binding: DirectiveBinding<MoneyOptions>) {
    // 获取原始值
    const rawValue = el.innerHTML?.trim()
    if (!rawValue) return

    // 获取配置项
    const { prefix = '', suffix = '' } = binding.value || {}

    // 移除可能存在的前缀、后缀、千分位符号
    const cleanValue = rawValue
      .replace(new RegExp(`^${prefix}`), '')
      .replace(new RegExp(`${suffix}$`), '')
      .replace(/,/g, '')

    if (cleanValue !== el._lastValue) {
      el._lastValue = cleanValue
      const formattedValue = formatMoney(Number(cleanValue), binding.value)
      el.innerHTML = formattedValue
    }
  }
}

export default moneyFormat
